import { ReactNode, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Layout as AntLayout, Menu, Button, Avatar, Typography, Modal } from 'antd'
import type { MenuProps } from 'antd'
import {
  MessageOutlined,
  MenuOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  DatabaseOutlined,
  ShareAltOutlined,
  HistoryOutlined,
  ToolOutlined,
  AppstoreOutlined,
  FileOutlined
} from '@ant-design/icons'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../contexts/ThemeContext'
import logoThiran from '../assets/thiran_white.svg'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchProjects } from '../store/slices/projectSlice'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { useSidebarConfig } from '../hooks/useSidebarConfig'
import ProjectToggle from './ProjectToggle'


const { Header, Sider, Content } = AntLayout
const { Text } = Typography

interface LayoutProps {
  children: ReactNode
}

const Layout = ({ children }: LayoutProps) => {
  const [collapsed, setCollapsed] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const { user, logout } = useAuth()
  const { themeConfig } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { currentProject } = useAppSelector((state) => state.project)
  const { isFeatureEnabled } = useFeatureFlags()

  // Determine current application from route
  const getCurrentApp = () => {
    if (location.pathname.startsWith('/admin/')) return 'admin'
    if (location.pathname.startsWith('/iam')) return 'iam'
    if (location.pathname.startsWith('/training/')) return 'training'
    if (location.pathname.startsWith('/demo/')) return 'demo'
    if (location.pathname.startsWith('/fallbackapp/')) return 'fallbackapp'
    return 'admin' // default
  }

  const currentApp = getCurrentApp()
  const { projectMenuItems, systemMenuItems, isLoading: sidebarLoading, error: sidebarError, refetch: refetchSidebar } = useSidebarConfig(currentApp)

  // Force refetch sidebar when route changes to app configuration
  useEffect(() => {
    if (location.pathname === '/admin/app-configuration') {
      // Small delay to ensure any config changes are saved first
      const timer = setTimeout(() => {
        refetchSidebar()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [location.pathname, refetchSidebar])

  useEffect(() => {
    dispatch(fetchProjects())
  }, [dispatch])

  // Fallback to old hardcoded menu items if dynamic config fails
  const fallbackProjectMenuItems = [
    ...(isFeatureEnabled('sidebar_data_sources') ? [{
      key: '/admin/projects',
      icon: <DatabaseOutlined />,
      label: 'Data Sources',
    }] : []),
    ...(isFeatureEnabled('ai_agent_builder') ? [{
      key: currentProject ? `/admin/projects/${currentProject.id}/state-graph` : '#',
      icon: <ShareAltOutlined />,
      label: 'AI Agent Builder',
      disabled: !currentProject,
    }] : []),
    ...(isFeatureEnabled('sidebar_query_logs') ? [{
      key: '/admin/query-logs',
      icon: <HistoryOutlined />,
      label: 'Query Logs',
    }] : []),
    ...(isFeatureEnabled('sidebar_file_management') ? [{
      key: '/admin/files',
      icon: <FileOutlined />,
      label: 'File Management',
    }] : []),
    ...(isFeatureEnabled('sidebar_chat') ? [{
      key: '/admin/chat',
      icon: <MessageOutlined />,
      label: 'Chat',
    }] : []),
    ...(isFeatureEnabled('maximo_integration') ? [{
      key: '/admin/maximo',
      icon: <ToolOutlined />,
      label: 'Maximo Work Orders',
    }] : []),
  ]

  const fallbackSystemMenuItems = [
    ...(isFeatureEnabled('app_configuration') ? [{
      key: '/admin/app-configuration',
      icon: <AppstoreOutlined />,
      label: 'App Configuration',
    }] : []),
    ...(isFeatureEnabled('sidebar_settings') ? [{
      key: '/admin/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    }] : []),
  ]


  // Use dynamic config if loaded successfully, otherwise fallback to hardcoded items
  // Only fallback if there's an error or if we're not loading and got empty results
  const shouldUseFallback = sidebarError || (!sidebarLoading && projectMenuItems.length === 0 && systemMenuItems.length === 0)
  
  let finalProjectMenuItems = shouldUseFallback ? fallbackProjectMenuItems : projectMenuItems
  let finalSystemMenuItems = shouldUseFallback ? fallbackSystemMenuItems : systemMenuItems

  // Debug logging
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    console.log('Layout sidebar state:', {
      sidebarLoading,
      sidebarError,
      shouldUseFallback,
      projectMenuItems: projectMenuItems.length,
      systemMenuItems: systemMenuItems.length,
      finalProjectMenuItems: finalProjectMenuItems.length,
      finalSystemMenuItems: finalSystemMenuItems.length,
    })
  }


  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
  }

  const handleLogout = () => {
    setShowLogoutModal(false)
    logout()
  }

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        collapsedWidth="0"
        width={215}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1000,
        }}
        trigger={null}
      >
        <div
          style={{
            height: '64px',
            background: themeConfig.primaryColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            padding: '8px 12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {collapsed ? (
              <img
                src={logoThiran}
                alt="Thiran"
                style={{
                  height: '32px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
            ) : (
              <img
                src={logoThiran}
                alt="ThiranAi"
                style={{
                  height: '40px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
            )}
          </div>
        </div>
        <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
          {/* Project Section - Hide for IAM app */}
          {currentApp !== 'iam' && (
            <div style={{ padding: '16px 16px 8px 16px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '8px'
              }}>
                {currentProject ? currentProject.name : 'No Project Selected'}
              </div>
            </div>
          )}

          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={finalProjectMenuItems as MenuProps['items']}
            onClick={handleMenuClick}
            style={{
              borderRight: 0,
              flex: 1,
              marginBottom: 'auto'
            }}
          />

          {/* System Settings */}
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={finalSystemMenuItems as MenuProps['items']}
            onClick={handleMenuClick}
            style={{
              borderRight: 0,
              borderTop: '1px solid #f0f0f0',
            }}
          />

          {/* Profile Section */}
          {!collapsed && (
            <div style={{
              borderTop: '1px solid #f0f0f0',
              padding: '16px',
              background: '#fafafa'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <Avatar
                  icon={<UserOutlined />}
                  size={40}
                  style={{
                    backgroundColor: themeConfig.primaryColor,
                    marginRight: '12px',
                    border: '2px solid #ffffff',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    flexShrink: 0
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#1f2937',
                    lineHeight: '20px'
                  }}>
                    <span>{user?.firstName} {user?.lastName}</span>
                    <span
                      style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '9px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        background: user?.role === 'admin' || user?.role === 'superadmin' 
                          ? '#52c41a' 
                          : '#1890ff',
                        color: '#ffffff',
                      }}
                    >
                      {user?.role || 'user'}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    lineHeight: '16px'
                  }}>
                    {user?.email}
                  </div>
                </div>
              </div>
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={() => setShowLogoutModal(true)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  height: '32px',
                  color: '#dc2626',
                  fontSize: '12px'
                }}
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </Sider>

      <AntLayout style={{ marginLeft: collapsed ? 0 : 215 }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 999,
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 40,
              height: 40,
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <ProjectToggle />
            <div style={{ height: '20px', width: '1px', backgroundColor: '#e5e7eb' }} />
            <Text style={{ color: '#6b7280', fontSize: '14px' }}>
              Welcome back, {user?.firstName}
            </Text>
          </div>
        </Header>

        <Content
          style={{
            padding: '24px',
            background: '#f3f4f6',
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
          }}
        >
          {children}
        </Content>
      </AntLayout>

      {/* Logout Confirmation Modal */}
      <Modal
        title="Confirm Logout"
        open={showLogoutModal}
        onOk={handleLogout}
        onCancel={() => setShowLogoutModal(false)}
        okText="Yes, Logout"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
      >
        <p>Are you sure you want to logout?</p>
      </Modal>
    </AntLayout>
  )
}

export default Layout